import type { Chiave } from "../../i18n/index.ts";
import { useT } from "../lib/i18n.tsx";
import { useState } from "react";
import { Badge, Button, Card, ErrorNotice, Field, Input, Spinner } from "../components/ui.tsx";
import { api } from "../lib/api.ts";
import { linkProps } from "../lib/router.ts";
import { useAction, useQuery } from "../lib/hooks.ts";
import type { Role, Session, UserAccount } from "../lib/types.ts";
import { formatoData } from "./Stacks.tsx";

const RUOLI: { valore: Role; descrizione: Chiave }[] = [
  { valore: "admin", descrizione: "utenti.ruoloAdmin" },
  { valore: "operator", descrizione: "utenti.ruoloOperator" },
  { valore: "viewer", descrizione: "utenti.ruoloViewer" },
];

export function SezioneUtenti({ sessione }: { sessione: Session }) {
  const { data, error, loading, reload } = useQuery<{ users: UserAccount[] }>("/users");
  const [creazioneAperta, setCreazioneAperta] = useState(false);
  const t = useT();

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-medium">{t.t("utenti.titolo")}</h2>
          <p className="text-sm tenue">{t.t("utenti.intro")}</p>
        </div>

        <Button variant="primary" onClick={() => setCreazioneAperta((aperta) => !aperta)}>
          {t.t(creazioneAperta ? "comune.annulla" : "utenti.nuovo")}
        </Button>
      </div>

      {creazioneAperta && (
        <FormNuovoUtente
          onCreato={() => {
            setCreazioneAperta(false);
            reload();
          }}
        />
      )}

      <ErrorNotice error={error} />
      {loading && <Spinner className="tenue" />}

      {data && (
        <Card className="divide-y overflow-hidden">
          {data.users.map((utente) => (
            <RigaUtente
              key={utente.id}
              utente={utente}
              seStesso={utente.id === sessione.user.id}
              onCambiato={reload}
            />
          ))}
        </Card>
      )}
    </section>
  );
}

function RigaUtente({
  utente,
  seStesso,
  onCambiato,
}: {
  utente: UserAccount;
  seStesso: boolean;
  onCambiato: () => void;
}) {
  const cambioRuolo = useAction(async (role: Role) => {
    await api.patch(`/users/${utente.id}`, { role });
    onCambiato();
  });

  const attivazione = useAction(async (isActive: boolean) => {
    await api.patch(`/users/${utente.id}`, { isActive });
    onCambiato();
  });

  const eliminazione = useAction(async () => {
    await api.delete(`/users/${utente.id}`);
    onCambiato();
  });

  const errore = cambioRuolo.error ?? attivazione.error ?? eliminazione.error;
  const t = useT();

  return (
    <div className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-medium">{utente.displayName ?? utente.username}</span>
        {utente.displayName && <span className="text-xs tenue">{utente.username}</span>}
        {seStesso && <Badge tone="accent">{t.t("utenti.seiTu")}</Badge>}
        {!utente.isActive && <Badge tone="warning">{t.t("utenti.disattivato")}</Badge>}

        <span className="ml-auto flex items-center gap-3">
          <span className="hidden text-xs tenue sm:inline">{t.t("utenti.dal", { quando: formatoData(utente.createdAt, t) })}</span>

          <select
            value={utente.role}
            disabled={cambioRuolo.running}
            onChange={(e) => void cambioRuolo.run(e.target.value as Role)}
            className="rounded-lg border bg-[var(--superficie)] px-2 py-1 text-sm"
          >
            {RUOLI.map((ruolo) => (
              <option key={ruolo.valore} value={ruolo.valore}>
                {ruolo.valore}
              </option>
            ))}
          </select>

          {/* I propri dati si cambiano dal Profilo, aperto anche a chi non gestisce gli utenti. */}
          {seStesso && (
            <a {...linkProps("/profilo")} className="text-xs text-accent-600 hover:underline">
              {t.t("utenti.modificaProfilo")}
            </a>
          )}

          {/* Disattivare sé stessi chiuderebbe fuori chi lo fa: come per l'eliminazione, non si offre. */}
          {!seStesso && (
            <Button
              variant="ghost"
              className="text-xs"
              loading={attivazione.running}
              onClick={() => {
                const nome = utente.displayName ?? utente.username;
                if (!utente.isActive || window.confirm(t.t("utenti.disattivaConferma", { nome }))) {
                  void attivazione.run(!utente.isActive);
                }
              }}
            >
              {t.t(utente.isActive ? "utenti.disattiva" : "utenti.riattiva")}
            </Button>
          )}

          {!seStesso && (
            <Button
              variant="ghost"
              className="text-xs text-red-600"
              loading={eliminazione.running}
              onClick={() => void eliminazione.run()}
            >
              {t.t("comune.elimina")}
            </Button>
          )}
        </span>
      </div>

      <ErrorNotice error={errore} />
    </div>
  );
}

function FormNuovoUtente({ onCreato }: { onCreato: () => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("operator");
  const t = useT();

  const creazione = useAction(async () => {
    await api.post("/users", { username, password, role });
    onCreato();
  });

  return (
    <Card className="space-y-4 p-5">
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label={t.t("accesso.utente")}>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} autoFocus />
        </Field>

        <Field label={t.t("accesso.password")} hint={t.t("utenti.passwordAiuto")}>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>

        <Field label={t.t("utenti.ruolo")} hint={t.t(RUOLI.find((r) => r.valore === role)!.descrizione)}>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="w-full rounded-lg border bg-[var(--superficie)] px-3 py-2 text-sm"
          >
            {RUOLI.map((ruolo) => (
              <option key={ruolo.valore} value={ruolo.valore}>
                {ruolo.valore}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <ErrorNotice error={creazione.error} />

      <Button variant="primary" loading={creazione.running} onClick={() => void creazione.run()}>
        {t.t("utenti.crea")}
      </Button>
    </Card>
  );
}
